import { coreAPI, SupplierCustomerRelationship } from '../../lib/api';

/** What an Assign Customer submit did, so the caller can say so. */
export type AssignOutcome = 'assigned' | 'code-added' | 'code-filled';

interface AssignCustomerCodeArgs {
  supplierCompanyId: number;
  customerId: number;
  /** The relationship this supplier already holds with the customer, if any. */
  existing: SupplierCustomerRelationship | undefined;
  /** Trimmed supplier code; '' when none was typed. */
  code: string;
  currency: string;
}

/**
 * Assign Customer: link the supplier to a customer, or give a customer it
 * already serves another supplier code.
 *
 * Every relationship gets a primary code seeded on commit from its own
 * supplier_code and currency (core.signals.ensure_primary_supplier_code).
 * Creating the relationship bare and then adding the typed code therefore left
 * two codes — the seeded one, blank because the supplier has no company-level
 * code, beside the typed one — so the screen showed two rows, one reading "-".
 * Sending the code with the relationship makes it the code that is seeded.
 *
 * For a customer already assigned, a live code with no value is that same
 * placeholder, so the typed code fills it rather than sitting beside it.
 *
 * @param args - The supplier, the customer, its existing relationship and the typed code.
 * @returns Which of the three things happened.
 */
export async function assignCustomerCode({
  supplierCompanyId, customerId, existing, code, currency,
}: AssignCustomerCodeArgs): Promise<AssignOutcome> {
  if (!existing) {
    await coreAPI.createSupplierRelationship({
      supplier: supplierCompanyId,
      customer: customerId,
      ...(code ? { supplier_code: code, currency } : {}),
    });
    return 'assigned';
  }
  const placeholder = (existing.supplier_codes ?? []).find((c) => c.is_active && !c.supplier_code);
  if (placeholder) {
    await coreAPI.updateSupplierCode(placeholder.id, { supplier_code: code, currency });
    return 'code-filled';
  }
  await coreAPI.createSupplierCode({ relationship: existing.id, supplier_code: code, currency });
  return 'code-added';
}
